import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search as SearchIcon, ArrowRight, BookOpen, FileText, Tag, Loader2, X } from 'lucide-react';
import AppShell from '@/components/AppShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Full-text search across units (title/description), vocabulary terms, and identifications.
// Lightweight client-side fan-out — units are pre-fetched once on mount.
export default function SearchPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initial = params.get('q') || '';
  const [query, setQuery] = useState(initial);
  const [allUnits, setAllUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(true);
  const [activeType, setActiveType] = useState('all'); // all | lessons | vocab | people

  const load = useCallback(async () => {
    setLoadingUnits(true);
    try {
      const results = await Promise.all(
        [7, 8, 9, 10, 11, 12].map(g => axios.get(`${API}/units/grade/${g}`).then(r => r.data).catch(() => []))
      );
      setAllUnits(results.flat());
    } catch (_e) {
      setAllUnits([]);
    } finally {
      setLoadingUnits(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Sync ?q= URL param as user types (debounced)
  useEffect(() => {
    const t = setTimeout(() => {
      if (query.trim()) setParams({ q: query.trim() }, { replace: true });
      else setParams({}, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [query, setParams]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return { lessons: [], vocab: [], people: [] };
    const lessons = [];
    const vocab = [];
    const people = [];
    allUnits.forEach(u => {
      const unitHit =
        u.title?.toLowerCase().includes(q) ||
        u.description?.toLowerCase().includes(q);
      if (unitHit) lessons.push({ unit: u });

      (u.vocabulary || []).forEach(v => {
        if ((v.term || '').toLowerCase().includes(q) || (v.definition || '').toLowerCase().includes(q)) {
          vocab.push({ unit: u, term: v });
        }
      });
      (u.identifications || []).forEach(p => {
        if ((p.name || '').toLowerCase().includes(q) ||
            (p.description || '').toLowerCase().includes(q) ||
            (p.significance || '').toLowerCase().includes(q)) {
          people.push({ unit: u, person: p });
        }
      });
    });
    return { lessons, vocab, people };
  }, [query, allUnits]);

  const totalCount = results.lessons.length + results.vocab.length + results.people.length;
  const visible = useMemo(() => {
    if (activeType === 'lessons') return { lessons: results.lessons, vocab: [], people: [] };
    if (activeType === 'vocab') return { lessons: [], vocab: results.vocab, people: [] };
    if (activeType === 'people') return { lessons: [], vocab: [], people: results.people };
    return results;
  }, [results, activeType]);

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-10" data-testid="search-page">
        {/* Header */}
        <div className="mb-6">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">Search</p>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-[#14213D] tracking-tight">Find anything in the archive.</h1>
          <p className="text-sm text-[#5A6577] mt-2">Across {allUnits.length} units, {allUnits.reduce((s, u) => s + (u.vocabulary?.length || 0), 0)} vocabulary terms, and key historical figures.</p>
        </div>

        {/* Search input */}
        <div className="relative mb-6">
          <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5A6577]" />
          <input
            type="text"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Try Mayflower, Hammurabi, Reconstruction, manifest destiny…"
            className="w-full bg-white border border-[#EBE7E0] rounded-full py-3 pl-11 pr-12 text-sm text-[#2D3748] placeholder:text-[#5A6577]/80 focus:outline-none focus:ring-2 focus:ring-[#14213D]/30 focus:border-[#14213D]/30 transition-all"
            data-testid="search-input"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-[#5A6577] hover:text-[#14213D]"
              aria-label="Clear search"
              data-testid="search-clear"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Filter pills */}
        {query.trim().length >= 2 && (
          <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="search-filters">
            {[
              { id: 'all', label: 'All', count: totalCount },
              { id: 'lessons', label: 'Lessons', count: results.lessons.length },
              { id: 'vocab', label: 'Vocabulary', count: results.vocab.length },
              { id: 'people', label: 'People & ideas', count: results.people.length },
            ].map(p => (
              <button
                key={p.id}
                onClick={() => setActiveType(p.id)}
                className={`text-xs px-3 py-1.5 rounded-full transition-all ${
                  activeType === p.id
                    ? 'bg-[#14213D] text-[#FDFBF7]'
                    : 'bg-white border border-[#EBE7E0] text-[#2D3748] hover:border-[#14213D]/30'
                }`}
                data-testid={`search-filter-${p.id}`}
              >
                {p.label} <span className={`text-[11px] ${activeType === p.id ? 'text-[#FDFBF7]/60' : 'text-[#5A6577]'}`}>{p.count}</span>
              </button>
            ))}
          </div>
        )}

        {/* Results */}
        {loadingUnits ? (
          <div className="flex flex-col items-center justify-center py-20 text-[#5A6577]">
            <Loader2 className="h-6 w-6 animate-spin mb-3" />
            <p className="text-sm">Indexing curriculum…</p>
          </div>
        ) : query.trim().length < 2 ? (
          <EmptyHint />
        ) : totalCount === 0 ? (
          <div className="text-center py-16 bg-[#F9F8F4] border border-[#EBE7E0] rounded-lg" data-testid="search-no-results">
            <SearchIcon className="h-8 w-8 text-[#E2E8F0] mx-auto mb-3" />
            <h3 className="font-serif-display text-lg text-[#14213D] mb-1">No results for &ldquo;{query}&rdquo;</h3>
            <p className="text-sm text-[#5A6577]">Try a broader term — eras, people, themes, or vocabulary.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {visible.lessons.length > 0 && (
              <Section title="Lessons" count={visible.lessons.length}>
                {visible.lessons.slice(0, 20).map(({ unit }) => (
                  <ResultCard
                    key={`u-${unit.id}`}
                    icon={BookOpen}
                    onOpen={() => navigate(`/unit/${unit.id}`)}
                    headline={unit.title}
                    blurb={unit.description}
                    tags={[`Grade ${unit.grade_level}`, `Unit ${unit.unit_number}`]}
                    testid={`result-lesson-${unit.id}`}
                  />
                ))}
              </Section>
            )}
            {visible.vocab.length > 0 && (
              <Section title="Vocabulary" count={visible.vocab.length}>
                {visible.vocab.slice(0, 20).map(({ unit, term }) => (
                  <ResultCard
                    key={`v-${unit.id}-${term.term}`}
                    icon={Tag}
                    onOpen={() => navigate(`/unit/${unit.id}`)}
                    headline={term.term}
                    blurb={term.definition}
                    tags={[`Grade ${unit.grade_level}`, unit.title]}
                    testid={`result-vocab-${unit.id}-${term.term.slice(0,18)}`}
                  />
                ))}
              </Section>
            )}
            {visible.people.length > 0 && (
              <Section title="People & ideas" count={visible.people.length}>
                {visible.people.slice(0, 20).map(({ unit, person }) => (
                  <ResultCard
                    key={`p-${unit.id}-${person.name}`}
                    icon={FileText}
                    onOpen={() => navigate(`/unit/${unit.id}`)}
                    headline={person.name}
                    blurb={person.significance || person.description}
                    tags={[`Grade ${unit.grade_level}`, unit.title, person.type || 'idea']}
                    testid={`result-person-${unit.id}-${person.name.slice(0,18)}`}
                  />
                ))}
              </Section>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}

const Section = ({ title, count, children }) => (
  <section>
    <header className="flex items-baseline justify-between mb-3 px-1">
      <h2 className="font-serif-display text-xl text-[#14213D] tracking-tight">{title}</h2>
      <span className="text-xs text-[#5A6577]">{count} {count === 1 ? 'result' : 'results'}{count > 20 ? ' (showing 20)' : ''}</span>
    </header>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
  </section>
);

const ResultCard = ({ icon: Icon, onOpen, headline, blurb, tags, testid }) => (
  <button
    onClick={onOpen}
    data-testid={testid}
    className="group text-left w-full bg-white border border-[#EBE7E0] rounded-md p-4 hover:-translate-y-0.5 hover:shadow-md hover:border-[#14213D]/20 transition-all relative overflow-hidden"
  >
    <div className="absolute top-0 left-0 w-full h-0.5 bg-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity" />
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-md bg-[#F9F8F4] flex items-center justify-center flex-shrink-0">
        <Icon className="h-4 w-4 text-[#7A93AC]" strokeWidth={1.75} />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="font-serif-display text-base text-[#14213D] leading-tight line-clamp-1 mb-1">{headline}</h3>
        <p className="text-xs text-[#5A6577] line-clamp-2 leading-relaxed mb-2">{blurb}</p>
        <div className="flex flex-wrap gap-1.5">
          {tags.map((t, i) => (
            <span key={`${t}-${i}`} className="text-[11px] bg-[#E2E8F0]/60 text-[#2D3748] px-1.5 py-0.5 rounded">{t}</span>
          ))}
        </div>
      </div>
      <ArrowRight className="h-4 w-4 text-[#E2E8F0] group-hover:text-[#14213D] transition-colors flex-shrink-0 mt-2" />
    </div>
  </button>
);

const EmptyHint = () => (
  <div className="text-center py-16">
    <SearchIcon className="h-10 w-10 text-[#E2E8F0] mx-auto mb-4" />
    <h3 className="font-serif-display text-lg text-[#14213D] mb-1">Start typing to search</h3>
    <p className="text-sm text-[#5A6577] max-w-md mx-auto">Search lesson titles, vocabulary, key figures and ideas across the entire archive.</p>
  </div>
);
